import React from 'react';
import { Route, Redirect, match, RouteComponentProps } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { useSelector } from 'react-redux';
import { History, Location } from 'history';
import asyncComponent from './helpers/AsyncFunc';
import Switch from 'react-router-dom/Switch';
import { App } from './containers/App/App';
import { AppState } from './redux/redux.model';
import Infra from './infra/infra';
import { isEmpty } from 'lodash';
import { Loading } from './components';
import ExternalLogoutRedirect from './containers/Page/sign/components/external-logout-redirect';

interface RouteRest {
  computedMatch?: match<Record<string, string | undefined>>;
  location?: Location;
  path: string;
}

interface RestrictedRouteProps {
  component: React.ComponentType<RouteComponentProps>;
  isLoggedIn: boolean;
  isLogoutSSO?: boolean | null;
  path: string;
}

const RestrictedRoute = ({ component: Component, isLoggedIn, isLogoutSSO, ...rest }: RestrictedRouteProps & RouteRest) => {
  return (
    <Route
      {...rest}
      render={(props: RouteComponentProps) => {
        if (isLoggedIn) {
          return <Component {...props} />;
        }

        if (isLogoutSSO) {
          return <Redirect to='/external-logout' />;
        }

        return (
          <Redirect
            to={{
              pathname: '/signin',
              state: { from: props.location }
            }}
          />
        );
      }}
    />
  );
};
interface PublicRoutesProps {
  history: History;
}

const PublicRoutes = ({ history }: PublicRoutesProps) => {
  const systemFlags = useSelector((state: AppState) => state.App.systemFlags);
  const isLoggedIn = useSelector((state: AppState) => state.Auth.isLogged);
  const isLogoutSSO = useSelector((state: AppState) => state.Auth.isLogoutSSO);

  const loginWithSSO = !!systemFlags?.P40_52117_login_with_SSO;

  const renderSignIn = () => {
    if (isEmpty(systemFlags)) return <Loading loading />;

    if (loginWithSSO) {
      const SignInSSO = asyncComponent(() => import('./containers/Page/sign/signin_SSO'));
      return <SignInSSO />;
    }
    const SignIn = asyncComponent(() => import('./containers/Page/sign/signin'));
    return <SignIn />;
  };

  return (
    <ConnectedRouter history={history}>
      <Infra>
        <Switch>
          <Route exact path='/signin' render={renderSignIn} />
          <Route exact path='/external-logout' render={() => <ExternalLogoutRedirect />} />
          <RestrictedRoute path='/' component={App} isLoggedIn={isLoggedIn} isLogoutSSO={isLogoutSSO} />
        </Switch>
      </Infra>
    </ConnectedRouter>
  );
};

export default PublicRoutes;
